'use client';

import { FC } from 'react';
import { useTranslations } from 'next-intl';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Lock } from 'lucide-react';
import { Spinner } from 'react-bootstrap';
import InputField from './InputField';
import PrimaryButton from './PrimaryButton';
import AuthFooter from './AuthFooter';
import shellStyles from './AuthShell.module.scss';
import styles from './PasswordScreen.module.scss';
interface PasswordScreenProps {
  firstName?: string;
  onSubmit: (password: string) => void | Promise<void>;
  onForgotPassword: () => void;
  isLoading?: boolean;
  serverError?: string;
  termLink?: string;
  privacyPolicyLink?: string;
}
const PasswordScreen: FC<PasswordScreenProps> = ({
  firstName,
  onSubmit,
  onForgotPassword,
  isLoading = false,
  serverError,
  termLink,
  privacyPolicyLink
}) => {
  const t = useTranslations();
  const schema = yup.object().shape({
    password: yup.string().required(t('auth.logIn.error'))
  });
  const {
    register,
    handleSubmit,
    trigger,
    formState: {
      errors
    }
  } = useForm<{
    password: string;
  }>({
    resolver: yupResolver(schema)
  });
  const submit = handleSubmit(async data => {
    await onSubmit(data.password);
  });
  return <>
            <div className={shellStyles.iconBubble}>
                <span className={shellStyles.iconBubbleInner}>
                    <Lock size={32} strokeWidth={1.75} data-sentry-element="Lock" data-sentry-source-file="PasswordScreen.tsx" />
                </span>
            </div>

            <h2 className={shellStyles.heading}>
                {t('auth.logIn.title')}
                {firstName ? ` ${firstName}` : ''}
            </h2>
            <p className={shellStyles.subtitle}>{t('auth.logIn.subtitle')}</p>

            <form className={styles.form} onSubmit={submit} noValidate>
                {serverError && <div className={styles.error}>{serverError}</div>}

                <InputField icon={<Lock size={16} strokeWidth={2} />} type="password" autoComplete="current-password" placeholder={t('auth.logIn.password')} showPasswordToggle error={errors.password?.message} {...register('password', {
        onBlur: () => trigger('password')
      })} data-sentry-element="InputField" data-sentry-source-file="PasswordScreen.tsx" />

                <button type="button" className={styles.forgot} onClick={onForgotPassword}>
                    {t('auth.logIn.forget')}
                </button>

                <PrimaryButton type="submit" disabled={isLoading} data-sentry-element="PrimaryButton" data-sentry-source-file="PasswordScreen.tsx">
                    {isLoading ? <Spinner animation="border" size="sm" /> : t('auth.logIn.submit')}
                </PrimaryButton>
            </form>

            <AuthFooter termLink={termLink} privacyPolicyLink={privacyPolicyLink} data-sentry-element="AuthFooter" data-sentry-source-file="PasswordScreen.tsx" />
        </>;
};
export default PasswordScreen;